import { Link } from "react-router-dom";
import { Cite } from "@/components/Cite";
import { motion } from "framer-motion";
import { FlaskConical, Repeat, Brain, Cog, Activity, BookOpen, ArrowUpRight, Laptop, Instagram } from "lucide-react";
import { scrollToId } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];
const reveal = (i = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.8, delay: i * 0.08, ease },
});

const PRINCIPLES = [
  [FlaskConical, "Evidenze, non mode", "Ogni indicazione nasce dalla letteratura scientifica, non dal trend del momento. Le guide HHL citano studi peer-reviewed e spiegano cosa dice davvero la ricerca."],
  [Repeat, "L'aderenza viene prima del programma", "Il principale predittore dei risultati a lungo termine non è la scheda perfetta, ma la capacità di seguirla nel tempo. Per questo lavoriamo su ciò che ti fa continuare.", ["teixeira2012", "burke2011"]],
  [Brain, "Le competenze mentali si allenano", "Motivazione, disciplina, gestione delle emozioni non sono tratti fissi del carattere: sono abilità che migliorano con la pratica, come la forza o la mobilità.", ["dweck2006"]],
  [Cog, "Sistemi, non forza di volontà", "Le persone più costanti non resistono di più alle tentazioni: organizzano la settimana e l'ambiente per incontrarne meno.", ["gollwitzer1999", "wood2007"]],
  [Activity, "Mente e corpo sono un unico sistema", "Postura, respiro, attenzione e linguaggio cambiano lo stato psicofisico. Allenare il corpo significa anche imparare a gestire chi lo guida.", ["nair2015", "zaccaro2018", "hatzigeorgiadis2011"]],
  [BookOpen, "Conoscere il proprio corpo", "Il corpo è l'abitacolo in cui viviamo tutta la vita: oggi non possiamo permetterci di ignorare come funziona. Educare è parte del percorso."],
];

const ECOSYSTEM = [
  [Laptop, "Online", "Il percorso di Training Online di 90 giorni seguito a distanza dal trainer HHL, in un gruppo a numero chiuso di 5 persone: un incontro di gruppo e un allenamento online a settimana.", "/training-online"],
  [Instagram, "In presenza in studio", "Allenamento in studio, seguito di persona. Per informazioni e disponibilità scrivimi su Instagram.", null, "https://www.instagram.com/hybridhealthlab/"],
];

const Principle = ({ p, i }) => {
  const [Icon, title, text, refs] = p;
  return (
    <motion.div {...reveal(i % 2)} data-testid={`about-principle-${i}`} className="border-t border-[rgba(152,191,162,0.2)] py-7">
      <div className="flex items-start gap-5">
        <span className="font-mono text-xs text-[#98bfa2]">0{i + 1}</span>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <Icon size={18} className="shrink-0 text-[#c4dcca]" />
            <h3 className="font-display text-3xl font-900 uppercase leading-none">{title}</h3>
          </div>
          <p className="mt-3 text-[#a3b1aa]">{text}<Cite refs={refs} /></p>
        </div>
      </div>
    </motion.div>
  );
};

const EcoCard = ({ e, i }) => {
  const [Icon, title, text, to, ig] = e;
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <Icon size={22} className="text-[#c4dcca]" />
        {to && <ArrowUpRight size={16} className="text-[#a3b1aa] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#c4dcca]" />}
      </div>
      <div className="mt-10">
        <h4 className="font-display text-2xl font-900 uppercase leading-none">{title}</h4>
        <p className="mt-2 text-sm text-[#a3b1aa]">{text}</p>
        {ig && (
          <a href={ig} target="_blank" rel="noreferrer" data-testid="about-studio-instagram-btn"
            className="group/ig mt-5 inline-flex items-center gap-2 bg-[#98bfa2] px-5 py-3 font-display text-base font-800 uppercase text-[#050807] transition-colors hover:bg-[#c4dcca]">
            <Instagram size={16} /> Scrivimi su Instagram
            <ArrowUpRight size={16} className="transition-transform group-hover/ig:-translate-y-0.5 group-hover/ig:translate-x-0.5" />
          </a>
        )}
      </div>
    </>
  );
  const cls = "group flex h-full flex-col justify-between border border-[rgba(152,191,162,0.2)] bg-[#0b120e] p-6 transition-colors hover:border-[#98bfa2]";
  return (
    <motion.div {...reveal(i)} className="h-full">
      {!to && <div data-testid={`about-eco-${i}`} className={cls}>{inner}</div>}
      {to?.startsWith("/") && <Link to={to} data-testid={`about-eco-${i}`} className={cls}>{inner}</Link>}
      {to && !to.startsWith("/") && <button onClick={() => scrollToId(to)} data-testid={`about-eco-${i}`} className={`${cls} w-full text-left`}>{inner}</button>}
    </motion.div>
  );
};

export const About = () => (
  <section id="chi-siamo" data-testid="about-section" className="px-5 py-24 md:px-10 md:py-36">
    <div className="mx-auto max-w-[1400px]">
      <motion.p {...reveal()} className="eyebrow mb-6">Hybrid Health Lab · Ricomposizione corporea · Evidenze, non mode</motion.p>
      <motion.h2 {...reveal(1)} className="max-w-5xl font-display text-5xl font-900 uppercase leading-[0.88] md:text-8xl">
        Il corpo cambia quando cambia <span className="text-[#c4dcca]">chi lo guida.</span>
      </motion.h2>

      <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
        <div className="space-y-6 text-base leading-relaxed text-[#a3b1aa] md:text-lg lg:sticky lg:top-28 lg:self-start">
          <motion.p {...reveal()}>
            <span className="text-[#f4f7f5]">In Hybrid Health Lab proponiamo un percorso di Lifestyle Training dedicato alla ricomposizione corporea</span>: ridurre il grasso, costruire muscolo e migliorare la salute in modo misurabile e sostenibile, senza scorciatoie e senza mode.
          </motion.p>
          <motion.p {...reveal(1)}>
            È <span className="text-[#f4f7f5]">ibrido</span> perché unisce due mondi che di solito restano separati. Da una parte le variabili tecniche — allenamento, nutrizione, recupero — che restano il punto di partenza. Dall'altra le competenze che decidono se un percorso dura: i valori che ti muovono, il modo in cui organizzi la settimana, come gestisci stress ed emozioni.
          </motion.p>
          <motion.p {...reveal(2)}>
            È un <span className="text-[#f4f7f5]">laboratorio</span> perché ogni scelta parte dai dati: si osserva, si misura, si corregge. Gli errori e le ricadute non sono fallimenti, sono informazioni.
          </motion.p>
          <motion.p {...reveal(3)}>
            L'obiettivo non è solo un fisico migliore, ma un sistema mentale che regge anche fuori dalla palestra: tra lavoro, famiglia, impegni e settimane storte.
          </motion.p>
          <motion.blockquote {...reveal(4)} data-testid="about-quote" className="border-l-2 border-[#98bfa2] pl-5">
            <p className="font-display text-2xl font-800 uppercase leading-tight text-[#f4f7f5] md:text-3xl">
              «Come faccio a trovare la motivazione per allenarmi?» La risposta onesta è: non la trovi… <span className="text-[#c4dcca]">la crei!</span>
            </p>
            <footer className="mt-3 font-mono text-xs uppercase tracking-widest text-[#98bfa2]">Matteo · Fondatore e trainer Hybrid Health Lab</footer>
          </motion.blockquote>
          <motion.div {...reveal(5)}>
            <Link to="/chi-sono" data-testid="about-story-link" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#c4dcca] underline-offset-4 hover:underline">
              Chi sono: leggi la mia storia <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </div>

        <div>
          <p className="eyebrow mb-2">I principi del lab</p>
          {PRINCIPLES.map((p, i) => <Principle key={p[1]} p={p} i={i} />)}
        </div>
      </div>

      <div className="mt-24">
        <p className="eyebrow mb-4">Come puoi lavorare con noi</p>
        <div className="grid max-w-5xl gap-4 md:grid-cols-2">
          {ECOSYSTEM.map((e, i) => <EcoCard key={e[1]} e={e} i={i} />)}
        </div>
      </div>
    </div>
  </section>
);
